import { VueConstructor } from 'vue'
import { bootstrap, setOptions, VueGtag } from 'vue-gtag';
import { Store } from 'vuex';
import { AsyncReturnType } from '../service/types/Utility'

import { DateTime, Duration } from 'luxon'

interface Gtag extends AsyncReturnType<typeof bootstrap> {
    (command: 'send', eventName: 'pageview' | 'event', eventParams: Record<string, string>): void;
}

interface GtagHitCounerParams {
    count: number;
    lastHit: number;
}

export class AnalyticsPlugin {
    private $store: Store<any>;
    private $gtag?: VueGtag;
    private counter = 0;
    private lastHit = 0;
    private enableLogging = process.env.NODE_ENV === 'development' && false
    private trackingId: string | undefined;
    private ga: Gtag;

    private MAX_HIT_COUNT = 499;
    private SESSION_TIMEOUT_MINUTES = 29;

    private windowChangeCallback?: () => void;

    constructor(store: Store<any>) {
        this.$store = store;

        this.initCounterIfNeeded();
        window.addEventListener('focus', this.windowChangeCallback = this.onWindowChange.bind(this));
        window.addEventListener('blur', this.windowChangeCallback);
    }

    private get storageKey() {
        return 'analytics_hit_counter';
    }

    install(Vue: VueConstructor, _: any) {
        Vue.prototype.$analytics = this;
    }

    uninstall() {
        this.updateGlobalCounter(this.getGlobalCounter());
        window.removeEventListener('focus', this.windowChangeCallback as () => void);
        window.removeEventListener('blur', this.windowChangeCallback as () => void);
    }

    async bootstrap(gtag: VueGtag) {
        const trackingId: string | undefined = this.$store.getters['services/getServiceParam']('googleAnalytics', 'trackingId');

        this.trackingId = trackingId;
        this.$gtag = gtag;

        if (trackingId) {
            setOptions({ config: { id: trackingId } });
            this.ga = await bootstrap() as Gtag;
        }
    }

    hitCounter() {
        if (this.lastHit) {
            const lastHitDiff = DateTime.fromMillis(this.lastHit).diffNow('minutes').minutes;
            const sessionExpired = Math.abs(lastHitDiff) > Duration.fromObject({ minutes: this.SESSION_TIMEOUT_MINUTES }).minutes;
            const counterParams = this.getGlobalCounter();

            if (sessionExpired) {
                //сброс счетчика по достижению максимального времени бездейтсвия
                this.resetGlobalCounter();
            }

            if (counterParams && counterParams.count >= this.MAX_HIT_COUNT) {
                //сброс счетчика по достижению максимального количества обращений к ga
                this.startNewGaSession();
                this.resetGlobalCounter();
            }
        }

        this.counter++;
        this.lastHit = Date.now();
    }

    private onWindowChange() {
        this.updateGlobalCounter(this.getGlobalCounter());
    }

    private updateGlobalCounter(hitCounterParams?: GtagHitCounerParams) {
        if (hitCounterParams) {
            this.setGlobalCounter({
                count: this.counter + hitCounterParams.count,
                lastHit: this.lastHit
            });

            this.counter = 0;
        }
    }

    private resetGlobalCounter() {
        this.setGlobalCounter({
            count: 0,
            lastHit: Date.now()
        });
    }

    private updateLocalCounter(hitCounterParams?: GtagHitCounerParams) {
        if (hitCounterParams) {
            this.counter = hitCounterParams.count;
            this.lastHit = hitCounterParams.lastHit;
        }
    }

    private initCounterIfNeeded() {
        const globalCounter = this.getGlobalCounter();

        if (globalCounter) {
            this.updateLocalCounter(globalCounter);
        }
        else {
            this.setGlobalCounter({
                count: this.counter,
                lastHit: this.lastHit
            });
        }
    }

    private setGlobalCounter(params: GtagHitCounerParams) {
        localStorage.setItem(this.storageKey, JSON.stringify(params));
    }

    private getGlobalCounter(): GtagHitCounerParams | undefined {
        try {
            return JSON.parse(localStorage.getItem(this.storageKey) || 'undefined');
        }
        catch(e) {
            return undefined;
        }
    }

    private startNewGaSession() {
        if (this.ga) {
            this.ga('send', 'pageview', {'sessionControl': 'start'});
        }
    }

    private completeCurrentGaSession() {
        if (this.ga) {
            this.ga('send', 'pageview', {'sessionControl': 'end'});
        }
    }

    /**
     *
     * @param startNewSession need start new ga session
     * @param endCurrentSession need complete current ga session
     */
    setGtagParams(startNewSession = false, endCurrentSession = false) {
        if(!this.trackingId) {
            //if counter not setted
            return
        }

        try {
            if (this.$gtag && process.env.NODE_ENV != 'development') {
                const backendString = this.$store.state.session?.backend;
                const userId = this.$store.getters['userId'] && backendString ? `${this.$store.getters['userId']}@${backendString}` : '-';
                const lang = this.$store.state.session?.language ?? 'ru';

                this.$gtag.set({
                    'user_id': userId,
                    'language': lang,
                    'app_name': 'Web',
                    'dimension1': this.$store.getters['userId'] ?? '-',
                    'dimension2': backendString || '-',
                    'dimension4': '-',
                    'dimension5': 'Web'
                });

                if (this.enableLogging) {
                    console.log('setting gtag params', window.google_tag_manager?.gcq?.i);
                }

                if (startNewSession) {
                    this.startNewGaSession();
                }
                else if (endCurrentSession) {
                    this.completeCurrentGaSession();
                }
            }
        }
        catch(e) {
            this.$store.dispatch('reportException', e, { root: true });
        }
    }
}
